/* eslint-disable no-unused-vars */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  Slider,
  Popover,
  Chip,
  // InputAdornment,
  // Autocomplete,
  // FormControl,
  // InputLabel,
  // Select,
  // MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator,
  Edit as EditIcon,
  FormatColorFill as FormatColorFillIcon,
  TextFields as TextFieldsIcon,
  Palette as PaletteIcon,
  // Add as AddIcon,
} from '@mui/icons-material';
import { useTimeline, TOOL_MODES } from './TimelineProvider';
import useThemeStyles from '../../hooks/useThemeStyles';

// Import corkboard colors for consistency
import corkboardColors from '../corkboards/corkboardColors.json';

const { noteColors, textColors, defaults: colorDefaults } = corkboardColors;

// Get array of color keys for the picker UI
const NOTE_COLOR_KEYS = Object.keys(noteColors);
const TEXT_COLOR_KEYS = Object.keys(textColors);

// Predefined categories for notes
const NOTE_CATEGORIES = [
  'General',
  'Character',
  'Plot',
  'Setting',
  'Research',
  'Worldbuilding',
  'Dialogue',
  'Theme',
  'Conflict',
  'Other',
];

/**
 * Resolve a color key to its actual hex value based on theme
 */
const resolveColor = (colorKey, colorMap, isDarkMode) => {
  if (!colorKey) return null;
  const colorEntry = colorMap[colorKey];
  if (colorEntry) {
    return isDarkMode ? colorEntry.dark : colorEntry.light;
  }
  if (colorKey.startsWith('#') || colorKey.startsWith('rgb')) {
    return colorKey;
  }
  return null;
};

/**
 * TimelineNote - A draggable, horizontally-resizable note on the timeline canvas
 */
const TimelineNote = ({ note }) => {
  const { colors, isDark } = useThemeStyles();
  const {
    moveNote,
    updateNote,
    deleteNote,
    resizeNoteWidth,
    bringNoteToFront,
    toolMode,
    viewState,
  } = useTimeline();

  // Get project chapters for autocomplete
  const activeProject = useSelector((state) => state.projects.activeProject);
  const projectChapters = useMemo(() => {
    const chapters = activeProject?.thinChapters || [];
    return chapters.map((ch) => ch.name || ch.title || `Chapter ${ch.id}`);
  }, [activeProject?.thinChapters]);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const [isResizingRight, setIsResizingRight] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(note.title || '');
  const [editContent, setEditContent] = useState(note.content || '');
  const [editCategory, setEditCategory] = useState(note.category || 'General');
  const [previewWidth, setPreviewWidth] = useState(null);
  const [previewX, setPreviewX] = useState(null);
  const [formatMenuAnchor, setFormatMenuAnchor] = useState(null);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [textColorMenuAnchor, setTextColorMenuAnchor] = useState(null);
  const [newChapter, setNewChapter] = useState('');
  const [newCharacter, setNewCharacter] = useState('');

  const dragStartPos = useRef({ x: 0, y: 0 });
  const noteStartPos = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0 });
  const noteStartWidth = useRef(0);
  const noteStartX = useRef(0);
  const noteRef = useRef(null);
  // Refs to track current resize values (avoid stale closure issues)
  const currentResizeWidth = useRef(null);
  const currentResizeX = useRef(null);

  const noteId = note._id || note.id || note.noteId;

  // Sync edit state when note changes
  useEffect(() => {
    if (!isEditing) {
      setEditTitle(note.title || '');
      setEditContent(note.content || '');
      setEditCategory(note.category || 'General');
      setNewChapter('');
      setNewCharacter('');
    }
  }, [note.title, note.content, note.category, isEditing]);

  // Handle clicks outside the note to cancel editing
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event) => {
      if (noteRef.current && !noteRef.current.contains(event.target)) {
        const { target } = event;
        if (
          target.closest('.MuiPopover-root') ||
          target.closest('.MuiMenu-root') ||
          target.closest('.MuiModal-root') ||
          target.closest('.MuiAutocomplete-popper') ||
          target.closest('.MuiAutocomplete-listbox') ||
          target.closest('.MuiAutocomplete-option')
        ) {
          return;
        }
        setIsEditing(false);
        setNewChapter('');
        setNewCharacter('');
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing]);

  const startEditing = useCallback(() => {
    if (toolMode !== TOOL_MODES.CURSOR) return;
    setIsEditing(true);
    setEditTitle(note.title || '');
    setEditContent(note.content || '');
    setEditCategory(note.category || 'General');
  }, [toolMode, note.title, note.content, note.category]);

  const handleContentClick = useCallback(
    (e) => {
      if (!isEditing && toolMode === TOOL_MODES.CURSOR) {
        e.stopPropagation();
        startEditing();
      }
    },
    [isEditing, toolMode, startEditing]
  );

  const handleHeaderMouseDown = useCallback(
    (e) => {
      if (toolMode !== TOOL_MODES.CURSOR || isEditing) return;

      if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();
      setIsDragging(true);

      dragStartPos.current = { x: e.clientX, y: e.clientY };
      noteStartPos.current = { x: note.x, y: note.y };

      let rafId = null;
      const handleMouseMove = (moveEvent) => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          const dx = (moveEvent.clientX - dragStartPos.current.x) / viewState.zoom;
          const dy = (moveEvent.clientY - dragStartPos.current.y) / viewState.zoom;

          moveNote(noteId, noteStartPos.current.x + dx, noteStartPos.current.y + dy);
          rafId = null;
        });
      };

      const handleMouseUp = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        bringNoteToFront(noteId);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [toolMode, isEditing, note.x, note.y, noteId, moveNote, viewState.zoom, bringNoteToFront]
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      startEditing();
    },
    [startEditing]
  );

  const handleSave = useCallback(() => {
    updateNote(noteId, {
      title: editTitle,
      content: editContent,
      category: editCategory,
      updatedEpoch: Date.now(),
    });
    setIsEditing(false);
    setNewChapter('');
    setNewCharacter('');
  }, [noteId, editTitle, editContent, editCategory, updateNote]);

  const handleAddChapter = useCallback(() => {
    if (newChapter.trim()) {
      const currentChapters = note.relatedChapters || [];
      const chapterName = newChapter.trim();
      const exists = currentChapters.some(
        (ch) => (typeof ch === 'string' ? ch : ch.name || ch.title) === chapterName
      );
      if (!exists) {
        updateNote(noteId, {
          relatedChapters: [...currentChapters, chapterName],
          updatedEpoch: Date.now(),
        });
        setNewChapter('');
      }
    }
  }, [newChapter, note.relatedChapters, noteId, updateNote]);

  const handleRemoveChapter = useCallback(
    (index) => {
      const currentChapters = [...(note.relatedChapters || [])];
      currentChapters.splice(index, 1);
      updateNote(noteId, {
        relatedChapters: currentChapters,
        updatedEpoch: Date.now(),
      });
    },
    [note.relatedChapters, noteId, updateNote]
  );

  const handleAddCharacter = useCallback(() => {
    if (newCharacter.trim()) {
      const currentCharacters = note.relatedCharacters || [];
      const characterName = newCharacter.trim();
      const exists = currentCharacters.some(
        (ch) => (typeof ch === 'string' ? ch : ch.name || ch.title) === characterName
      );
      if (!exists) {
        updateNote(noteId, {
          relatedCharacters: [...currentCharacters, characterName],
          updatedEpoch: Date.now(),
        });
        setNewCharacter('');
      }
    }
  }, [newCharacter, note.relatedCharacters, noteId, updateNote]);

  const handleRemoveCharacter = useCallback(
    (index) => {
      const currentCharacters = [...(note.relatedCharacters || [])];
      currentCharacters.splice(index, 1);
      updateNote(noteId, {
        relatedCharacters: currentCharacters,
        updatedEpoch: Date.now(),
      });
    },
    [note.relatedCharacters, noteId, updateNote]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        setIsEditing(false);
      } else if (e.key === 'Enter' && e.ctrlKey) {
        handleSave();
      }
    },
    [handleSave]
  );

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      deleteNote(noteId);
    },
    [noteId, deleteNote]
  );

  // Handle left edge resize
  const handleResizeLeftMouseDown = useCallback(
    (e) => {
      if (toolMode !== TOOL_MODES.CURSOR || isEditing) return;

      e.stopPropagation();
      e.preventDefault();
      setIsResizingLeft(true);

      resizeStartPos.current = { x: e.clientX };
      noteStartWidth.current = note.width || 200;
      noteStartX.current = note.x;
      currentResizeWidth.current = null;
      currentResizeX.current = null;

      const handleMouseMove = (moveEvent) => {
        const dx = (moveEvent.clientX - resizeStartPos.current.x) / viewState.zoom;
        const newWidth = Math.max(150, noteStartWidth.current - dx);
        const newX = noteStartX.current + (noteStartWidth.current - newWidth);

        // Update both state (for UI) and refs (for mouseUp)
        currentResizeWidth.current = newWidth;
        currentResizeX.current = newX;
        setPreviewWidth(newWidth);
        setPreviewX(newX);
      };

      const handleMouseUp = () => {
        // Use refs to get the latest values
        if (currentResizeWidth.current !== null && currentResizeX.current !== null) {
          resizeNoteWidth(noteId, currentResizeWidth.current, currentResizeX.current);
        }
        currentResizeWidth.current = null;
        currentResizeX.current = null;
        setIsResizingLeft(false);
        setPreviewWidth(null);
        setPreviewX(null);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [toolMode, isEditing, note.width, note.x, noteId, resizeNoteWidth, viewState.zoom]
  );

  // Handle right edge resize
  const handleResizeRightMouseDown = useCallback(
    (e) => {
      if (toolMode !== TOOL_MODES.CURSOR || isEditing) return;

      e.stopPropagation();
      e.preventDefault();
      setIsResizingRight(true);

      resizeStartPos.current = { x: e.clientX };
      noteStartWidth.current = note.width || 200;
      currentResizeWidth.current = null;

      const handleMouseMove = (moveEvent) => {
        const dx = (moveEvent.clientX - resizeStartPos.current.x) / viewState.zoom;
        const newWidth = Math.max(150, noteStartWidth.current + dx);
        // Update both state (for UI) and ref (for mouseUp)
        currentResizeWidth.current = newWidth;
        setPreviewWidth(newWidth);
      };

      const handleMouseUp = () => {
        // Use ref to get the latest value
        if (currentResizeWidth.current !== null) {
          resizeNoteWidth(noteId, currentResizeWidth.current);
        }
        currentResizeWidth.current = null;
        setIsResizingRight(false);
        setPreviewWidth(null);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [toolMode, isEditing, note.width, noteId, resizeNoteWidth, viewState.zoom]
  );

  // Memoize cursor styles
  const headerCursorStyle = useMemo(() => {
    if (toolMode !== TOOL_MODES.CURSOR) return 'default';
    if (isDragging) return 'grabbing';
    return 'grab';
  }, [toolMode, isDragging]);

  const contentCursorStyle = useMemo(() => {
    if (isEditing) return 'default';
    if (toolMode === TOOL_MODES.CURSOR) return 'text';
    return 'default';
  }, [isEditing, toolMode]);

  // Get current dimensions
  const currentWidth = useMemo(() => previewWidth ?? note.width ?? 200, [previewWidth, note.width]);
  const currentX = useMemo(() => previewX ?? note.x, [previewX, note.x]);
  const fixedHeight = 200;

  // Calculate font size
  const fontSizeMultiplier = useMemo(
    () => note.fontSizeMultiplier ?? 1.0,
    [note.fontSizeMultiplier]
  );
  const { calculatedFontSize } = useMemo(() => {
    const base = Math.max(10, Math.min(20, (currentWidth / 200) * 14));
    return {
      calculatedFontSize: base * fontSizeMultiplier,
    };
  }, [currentWidth, fontSizeMultiplier]);

  // Get colors with theme-awareness
  const noteColorKey = note.color || colorDefaults.note;
  const textColorKey = note.textColor || colorDefaults.text;

  const noteColor = useMemo(
    () => resolveColor(noteColorKey, noteColors, isDark) || (isDark ? '#424242' : '#fef3c7'),
    [noteColorKey, isDark]
  );
  const textColor = useMemo(
    () =>
      resolveColor(textColorKey, textColors, isDark) ||
      (isDark ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)'),
    [textColorKey, isDark]
  );

  // Handle font size change
  const handleFontSizeChange = useCallback(
    (event, newValue) => {
      updateNote(noteId, {
        fontSizeMultiplier: newValue,
        updatedEpoch: Date.now(),
      });
    },
    [noteId, updateNote]
  );

  // Handle background color change
  const handleColorChange = useCallback(
    (colorKey) => {
      updateNote(noteId, {
        color: colorKey,
        updatedEpoch: Date.now(),
      });
      setColorMenuAnchor(null);
    },
    [noteId, updateNote]
  );

  // Handle text color change
  const handleTextColorChange = useCallback(
    (colorKey) => {
      updateNote(noteId, {
        textColor: colorKey,
        updatedEpoch: Date.now(),
      });
      setTextColorMenuAnchor(null);
    },
    [noteId, updateNote]
  );

  return (
    <Paper
      ref={noteRef}
      elevation={isDragging || isResizingLeft || isResizingRight || isEditing ? 8 : 3}
      sx={{
        position: 'absolute',
        left: currentX,
        top: note.y,
        width: currentWidth,
        height: fixedHeight,
        backgroundColor: noteColor,
        transition: isDragging || isResizingLeft || isResizingRight ? 'none' : 'box-shadow 0.2s',
        zIndex:
          isDragging || isResizingLeft || isResizingRight || isEditing ? 1000 : note.zIndex || 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        userSelect: isEditing ? 'text' : 'none',
      }}
      onDoubleClick={handleDoubleClick}
      data-timeline-note
    >
      {/* Left Resize Handle */}
      {toolMode === TOOL_MODES.CURSOR && !isEditing && (
        <Box
          onMouseDown={handleResizeLeftMouseDown}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 10,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
          }}
        />
      )}

      {/* Right Resize Handle */}
      {toolMode === TOOL_MODES.CURSOR && !isEditing && (
        <Box
          onMouseDown={handleResizeRightMouseDown}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            zIndex: 10,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
          }}
        />
      )}

      {/* Header / Drag Handle */}
      <Box
        onMouseDown={handleHeaderMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          cursor: headerCursorStyle,
        }}
      >
        <DragIndicator
          sx={{ fontSize: 18, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)' }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {toolMode === TOOL_MODES.CURSOR && !isEditing && (
            <>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormatMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Format"
              >
                <TextFieldsIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setColorMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Background Color"
              >
                <FormatColorFillIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setTextColorMenuAnchor(e.currentTarget);
                }}
                sx={{
                  p: 0.25,
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  },
                }}
                title="Text Color"
              >
                <PaletteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={handleDelete}
            sx={{
              p: 0.25,
              color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255,0,0,0.1)',
                color: 'error.main',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 1,
          flexGrow: 1,
          cursor: contentCursorStyle,
          overflow: 'auto',
        }}
        onClick={handleContentClick}
        onDoubleClick={handleDoubleClick}
      >
        {isEditing ? (
          <Box onKeyDown={handleKeyDown} onClick={(e) => e.stopPropagation()}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Title"
              autoFocus
              sx={{ mb: 1 }}
              InputProps={{
                sx: {
                  fontWeight: 'bold',
                  fontSize: `${calculatedFontSize * 1.1}px`,
                  color: textColor,
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              size="small"
              variant="outlined"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Content..."
              sx={{ mb: 1 }}
              InputProps={{
                sx: {
                  fontSize: `${calculatedFontSize}px`,
                  color: textColor,
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
            />

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setIsEditing(false)}
                sx={{ minWidth: 60 }}
              >
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={handleSave} sx={{ minWidth: 60 }}>
                Save
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  color: textColor,
                  wordBreak: 'break-word',
                  flexGrow: 1,
                  fontSize: `${calculatedFontSize * 1.1}px`,
                  lineHeight: 1.2,
                }}
              >
                {note.title || 'Untitled'}
              </Typography>
              {toolMode === TOOL_MODES.CURSOR && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing();
                  }}
                  sx={{
                    p: 0.25,
                    ml: 0.5,
                    opacity: 0.6,
                    color: isDark ? 'rgba(255,255,255,0.7)' : 'inherit',
                    '&:hover': {
                      opacity: 1,
                      backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: textColor,
                fontSize: `${calculatedFontSize * 0.9}px`,
                lineHeight: 1.4,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {note.content || ''}
            </Typography>

            {/* Category */}
            {note.category && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={note.category}
                  size="small"
                  sx={{
                    backgroundColor: isDark ? 'rgba(100,150,255,0.2)' : 'rgba(25,118,210,0.1)',
                    color: textColor,
                    fontSize: `${calculatedFontSize * 0.65}px`,
                    height: 18,
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Format Menu Popover */}
      <Popover
        open={Boolean(formatMenuAnchor)}
        anchorEl={formatMenuAnchor}
        onClose={() => setFormatMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography
            variant="caption"
            sx={{ mb: 1, display: 'block', fontWeight: 'bold', color: colors.text.primary }}
          >
            Font Size
          </Typography>
          <Slider
            value={fontSizeMultiplier}
            onChange={handleFontSizeChange}
            min={0.5}
            max={2.0}
            step={0.1}
            marks={[
              { value: 0.5, label: '50%' },
              { value: 1.0, label: '100%' },
              { value: 1.5, label: '150%' },
              { value: 2.0, label: '200%' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
          />
        </Box>
      </Popover>

      {/* Background Color Picker Popover */}
      <Popover
        open={Boolean(colorMenuAnchor)}
        anchorEl={colorMenuAnchor}
        onClose={() => setColorMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
          {NOTE_COLOR_KEYS.map((colorKey) => {
            const displayColor = resolveColor(colorKey, noteColors, isDark);
            const isSelected = noteColorKey === colorKey;
            return (
              <Box
                key={colorKey}
                onClick={() => handleColorChange(colorKey)}
                title={colorKey}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: displayColor,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  '&:hover': { transform: 'scale(1.1)' },
                  transition: 'transform 0.2s',
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Text Color Picker Popover */}
      <Popover
        open={Boolean(textColorMenuAnchor)}
        anchorEl={textColorMenuAnchor}
        onClose={() => setTextColorMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#2d2d2d' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          },
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', width: 200, gap: 0.5 }}>
          {TEXT_COLOR_KEYS.map((colorKey) => {
            const displayColor = resolveColor(colorKey, textColors, isDark);
            const isSelected = textColorKey === colorKey;
            return (
              <Box
                key={colorKey}
                onClick={() => handleTextColorChange(colorKey)}
                title={colorKey}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: displayColor,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                  '&:hover': { transform: 'scale(1.1)' },
                  transition: 'transform 0.2s',
                }}
              />
            );
          })}
        </Box>
      </Popover>
    </Paper>
  );
};

TimelineNote.propTypes = {
  note: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    noteId: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    category: PropTypes.string,
    color: PropTypes.string,
    textColor: PropTypes.string,
    fontSizeMultiplier: PropTypes.number,
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    zIndex: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    relatedChapters: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ name: PropTypes.string, title: PropTypes.string }),
      ])
    ),
    relatedCharacters: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ name: PropTypes.string, title: PropTypes.string }),
      ])
    ),
    relatedNotes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default React.memo(TimelineNote, (prevProps, nextProps) => {
  const prevNote = prevProps.note;
  const nextNote = nextProps.note;

  return (
    prevNote._id === nextNote._id &&
    prevNote.x === nextNote.x &&
    prevNote.y === nextNote.y &&
    prevNote.width === nextNote.width &&
    prevNote.zIndex === nextNote.zIndex &&
    prevNote.title === nextNote.title &&
    prevNote.content === nextNote.content &&
    prevNote.color === nextNote.color &&
    prevNote.textColor === nextNote.textColor &&
    prevNote.fontSizeMultiplier === nextNote.fontSizeMultiplier &&
    prevNote.category === nextNote.category
  );
});
